import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useChangeSolarPlantStatusMutation,
  useLazyViewCustomerPlantDetailsQuery,
} from "../../../redux/api/generation/generationAPI";
import IViewUserSolarPlantDetailsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-user-solar-plant-details-response-dto";
import { EPlantStatus } from "../../../shared/oversight-core/enums/plant-status";
import { ESolarServiceProvider } from "../../../shared/oversight-core/enums/solar-service-provider";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { IVrmSolarPlantView } from "../../../shared/oversight-core/interfaces/vrm-solar-plant-view";
import ActiveInactiveIndicator from "../../../shared/oversight-core/ui-elements/active-inactive-indicator/active-inactive-indicator";
import AppDropDown from "../../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import AppTabs from "../../../shared/oversight-core/ui-elements/app-tabs/app-tabs";
import ArrowButton from "../../../shared/oversight-core/ui-elements/arrow-button/arrow-button";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { getServiceProviderLogo } from "../../../shared/oversight-core/utils/get-service-provider-logo";
import { getRelativeTimeFromTimestamp } from "../../../shared/oversight-core/utils/time-utils";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";
import translate from "../../../shared/oversight-core/utils/translate";
import { IGoodWeSolarPlantView } from "./../../../shared/oversight-core/interfaces/good-we-solar-plant-view";
import GoodWeSiteDashboard from "./components/site-dashboard/good-we-site-dashboard";
import VrmSiteDashboard from "./components/site-dashboard/vrm-site-dashboard";
import SiteDevices from "./components/site-devices/site-devices";
import SiteErrorLog from "./components/site-error-log/site-error-log";
import DisableSiteModal from "./disable-site-modal/disable-site-modal";

const ViewSite = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const plantId = location.state.id as string;
  const serviceProvider = location.state
    .serviceProvider as ESolarServiceProvider;
  const activeTabState = location.state.activeTab as number;

  const [activeTab, setActiveTab] = useState<number>(1);
  const [vrmSolarPlant, setVrmSolarPlant] = useState<IVrmSolarPlantView>();
  const [goodWeSolarPlant, setGoodWeSolarPlant] =
    useState<IGoodWeSolarPlantView>();
  const [isViewingSolarPlant, setIsViewingSolarPlant] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDisableSiteModal, setShowDisableSiteModal] = useState(false);

  const [triggerCustomerPlantDetails] = useLazyViewCustomerPlantDetailsQuery();
  const [
    triggerChangeSolarPlantStatus,
    { isLoading: isLoadingChangeSolarPlantStatus },
  ] = useChangeSolarPlantStatusMutation();

  useEffect(() => {
    if (activeTabState) {
      setActiveTab(activeTabState);
    }
  }, [activeTabState]);

  useEffect(() => {
    if (plantId) {
      triggerCustomerPlantDetails({ plantId })
        .unwrap()
        .then((res: IViewUserSolarPlantDetailsResponseDTO) => {
          setVrmSolarPlant(res.solarPlantView as IVrmSolarPlantView);
          setGoodWeSolarPlant(res.solarPlantView as IGoodWeSolarPlantView);
          setIsViewingSolarPlant(true);
        })
        .catch(() => {
          setVrmSolarPlant(undefined);
          setGoodWeSolarPlant(undefined);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [triggerCustomerPlantDetails, plantId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (plantId) {
        triggerCustomerPlantDetails({ plantId })
          .unwrap()
          .then((res: IViewUserSolarPlantDetailsResponseDTO) => {
            setVrmSolarPlant(res.solarPlantView as IVrmSolarPlantView);
            setGoodWeSolarPlant(res.solarPlantView as IGoodWeSolarPlantView);
            setIsViewingSolarPlant(true);
          })
          .catch(() => {
            setVrmSolarPlant(undefined);
            setGoodWeSolarPlant(undefined);
          });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [triggerCustomerPlantDetails, plantId]);

  const getActiveTab = useMemo(() => {
    switch (activeTab) {
      case 2:
        return (
          <SiteDevices plantId={plantId} serviceProvider={serviceProvider} />
        );
      case 3:
        return (
          <SiteErrorLog
            plantId={plantId}
            zoneId={
              serviceProvider === ESolarServiceProvider.VRM
                ? vrmSolarPlant?.tz.formattedValue
                : ""
            }
            serviceProvider={serviceProvider}
          />
        );
      default:
        return serviceProvider === ESolarServiceProvider.VRM ? (
          <VrmSiteDashboard
            plantId={plantId}
            solarPlant={vrmSolarPlant as IVrmSolarPlantView}
            isViewingSolarPlant={isViewingSolarPlant}
          />
        ) : (
          <GoodWeSiteDashboard
            plantId={plantId}
            solarPlant={goodWeSolarPlant as IGoodWeSolarPlantView}
          />
        );
    }
  }, [
    activeTab,
    plantId,
    serviceProvider,
    vrmSolarPlant,
    isViewingSolarPlant,
    goodWeSolarPlant,
  ]);

  const disableSite = (plantId: string) => {
    triggerChangeSolarPlantStatus({ plantId })
      .unwrap()
      .then(() => {
        showSuccessMessage(
          (
            serviceProvider === ESolarServiceProvider.VRM
              ? vrmSolarPlant?.isActive
              : goodWeSolarPlant?.isActive
          )
            ? "Site disable and hide successfully"
            : "Site enable successfully"
        );
        navigate(AppRoute.ADMIN_SITES, {
          state: {
            updateCurrentState: true,
          },
        });
      })
      .catch(() => {
        showErrorMessage(
          (
            serviceProvider === ESolarServiceProvider.VRM
              ? vrmSolarPlant?.isActive
              : goodWeSolarPlant?.isActive
          )
            ? "Site disable and hide unsuccessfully"
            : "Site enable unsuccessfully"
        );
      });
  };

  return (
    <div className="position-relative">
      {isLoading ? (
        <SpinnerModal show={isLoading} />
      ) : (
        <>
          <Row className="align-items-center justify-content-between">
            <Col className="col-12 col-lg-auto">
              <Row className="align-items-center ms-0">
                <Col className="col-auto">
                  <ArrowButton onBack={() => navigate(-1)} />
                </Col>
                <Col>
                  <Row className="align-items-center">
                    <Col className="col-auto pe-0 text-dark font-weight-500 font-size-24">
                      {serviceProvider === ESolarServiceProvider.VRM
                        ? vrmSolarPlant?.name?.toString() ?? ""
                        : goodWeSolarPlant?.plantname}
                    </Col>
                    <Col>
                      <ActiveInactiveIndicator
                      // isActive={solarPlant.status === EPlantStatus.GENERATING}
                      />
                    </Col>
                  </Row>
                  <Row className="row-cols-auto align-items-center">
                    <Col className="pe-0">
                      <img
                        src={getServiceProviderLogo(serviceProvider)}
                        alt=""
                      />
                    </Col>
                    {serviceProvider === ESolarServiceProvider.VRM && (
                      <Col className="text-light font-weight-400 font-size-12">
                        Last Update&nbsp;
                        <span className="text-dark">
                          {vrmSolarPlant?.last_timestamp ? (
                            <>
                              {getRelativeTimeFromTimestamp(
                                vrmSolarPlant?.last_timestamp as unknown as number
                              )}
                            </>
                          ) : (
                            ``
                          )}
                        </span>
                      </Col>
                    )}
                    <Col className="text-light font-weight-400 font-size-12">
                      Status&nbsp;
                      <span className="text-dark">
                        {serviceProvider === ESolarServiceProvider.VRM
                          ? vrmSolarPlant?.ss.formattedValue
                          : translate(
                              goodWeSolarPlant?.plantStatus as EPlantStatus
                            )}
                      </span>
                    </Col>
                    <Col className="text-light font-weight-400 font-size-12">
                      Local Time&nbsp;
                      <span className="text-dark">
                        {serviceProvider === ESolarServiceProvider.VRM
                          ? DateTime.now()
                              .setZone(
                                vrmSolarPlant?.tz.formattedValue
                                  ? vrmSolarPlant?.tz.formattedValue
                                  : "Etc/UTC"
                              )
                              .toFormat("HH:mm a")
                          : DateTime.now()
                              .setZone("Etc/UTC")
                              .toFormat("HH:mm a")}
                      </span>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col className="col-12 col-lg-5 col-xxl-4 mt-2 mt-xxl-0">
              <Row className="align-items-center">
                <Col className="col-auto pe-0">
                  <AppDropDown
                    items={[
                      {
                        text: (
                          serviceProvider === ESolarServiceProvider.VRM
                            ? vrmSolarPlant?.isActive
                            : goodWeSolarPlant?.isActive
                        )
                          ? "Disable"
                          : "Enable",
                        onClick: () => setShowDisableSiteModal(true),
                      },
                    ]}
                    size="md"
                  />
                </Col>
                <Col>
                  <AppTabs
                    buttons={[
                      {
                        buttonName: "Dashboard",
                        selectedColor: "blueColor",
                        id: 1,
                      },
                      {
                        buttonName: "Devices",
                        selectedColor: "blueColor",
                        id: 2,
                      },
                      {
                        buttonName: "Error Log",
                        selectedColor: "blueColor",
                        id: 3,
                      },
                    ]}
                    onChange={(id) => {
                      setActiveTab(id);
                    }}
                    selectedTabId={activeTab}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>{getActiveTab}</Col>
          </Row>
        </>
      )}
      <DisableSiteModal
        show={showDisableSiteModal}
        onClose={() => setShowDisableSiteModal(false)}
        onCancel={() => setShowDisableSiteModal(false)}
        siteName={
          serviceProvider === ESolarServiceProvider.VRM
            ? vrmSolarPlant?.name?.toString() ?? ""
            : goodWeSolarPlant?.plantname || ""
        }
        onConfirm={() => {
          if (
            serviceProvider === ESolarServiceProvider.VRM &&
            vrmSolarPlant?.identity
          ) {
            disableSite(vrmSolarPlant?.identity.toString());
            return;
          }

          if (
            serviceProvider === ESolarServiceProvider.GOODWE &&
            goodWeSolarPlant?.identity
          ) {
            disableSite(goodWeSolarPlant?.identity.toString());
            return;
          }
        }}
        isLoading={isLoadingChangeSolarPlantStatus}
        isActive={
          serviceProvider === ESolarServiceProvider.VRM
            ? typeof vrmSolarPlant?.isActive === "boolean"
              ? vrmSolarPlant.isActive
              : undefined
            : typeof goodWeSolarPlant?.isActive === "boolean"
            ? goodWeSolarPlant.isActive
            : undefined
        }
      />
    </div>
  );
};

export default ViewSite;
